<template>
	<v-card data-testid = "dashboard-alerts-by-device-heatmap-widget" dark outlined class = "dashboard-alerts-by-device-heatmap-widget fill-height">
		<v-system-bar lights-out dark class = "vue-draggable-handle" style = "cursor:move">
			<v-icon small color = "red lighten-2">mdi-map-marker-alert</v-icon>
			<span class = "white--text subtitle-2 text-uppercase ml-1 text-truncate">{{ widgetTitle }}</span>
			<v-spacer></v-spacer>
			<span v-if = "generatedAt" class = "caption grey--text text-truncate mr-2">
				{{ $ml.get('dashboard_pie_events_generated_at') }}: {{ generatedAt }}
			</span>
			<v-btn icon x-small class = "no-drag" :disabled = "loading" @click = "refresh">
				<v-icon x-small color = "white">mdi-refresh</v-icon>
			</v-btn>
			<v-btn icon x-small class = "error ml-1 no-drag" @click = "$emit('close_component', layout_id)">
				<v-icon x-small color = "white">mdi-close</v-icon>
			</v-btn>
		</v-system-bar>

		<div class = "dashboard-alerts-by-device-heatmap-widget__body no-drag">
			<v-overlay v-if = "loading && !hasDevices" absolute z-index = "10">
				<div class = "text-center">
					<v-progress-circular indeterminate size = "44" color = "red lighten-2"></v-progress-circular>
					<div class = "mt-3 caption grey--text">{{ $ml.get('dashboard_alerts_by_device_loading') }}</div>
				</div>
			</v-overlay>

			<v-alert v-if = "unsupportedSchema" type = "warning" dense prominent class = "ma-2">
				{{ $ml.get('dashboard_pie_events_unsupported_config') }}
			</v-alert>

			<v-alert v-else-if = "error" type = "error" dense prominent class = "ma-2">
				<v-row align = "center" no-gutters>
					<v-col class = "grow">{{ error }}</v-col>
					<v-col class = "shrink">
						<v-btn small outlined color = "white" class = "ml-3" @click = "refresh">
							<v-icon small left>mdi-refresh</v-icon>
							{{ $ml.get('dashboard_pie_events_retry') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-alert>

			<div v-else class = "dashboard-alerts-by-device-heatmap-widget__content">
				<div class = "dashboard-alerts-by-device-heatmap-widget__intervals">
					<span class = "caption grey--text text-uppercase">{{ $ml.get('dashboard_operator_action_time_interval') }}</span>
					<v-btn-toggle v-model = "selectedInterval" mandatory dense dark class = "dashboard-alerts-by-device-heatmap-widget__interval-toggle">
						<v-btn v-for = "option in intervalOptions" :key = "option.value" x-small :value = "option.value" :disabled = "loading">
							{{ $ml.get(option.labelKey) }}
						</v-btn>
					</v-btn-toggle>
				</div>

				<v-tabs v-model = "activeView" background-color = "transparent" color = "red lighten-2" dark height = "30" class = "dashboard-alerts-by-device-heatmap-widget__tabs">
					<v-tab href = "#map">{{ $ml.get('dashboard_alerts_by_device_tab_map') }}</v-tab>
					<v-tab href = "#list">{{ $ml.get('dashboard_alerts_by_device_tab_list') }}</v-tab>
				</v-tabs>

				<div v-show = "activeView === 'map'" class = "dashboard-alerts-by-device-heatmap-widget__map-row">
					<div class = "dashboard-alerts-by-device-heatmap-widget__map-panel">
						<l-map v-if = "hasMappableDevices" ref = "map" :center = "mapCenter" :zoom = "mapZoom" class = "dashboard-alerts-by-device-heatmap-widget__leaflet">
							<l-tile-layer :url = "tileUrl" :attribution = "tileAttribution"></l-tile-layer>
							<l-circle-marker
								v-for = "device in mappableDevices"
								:key = "device.deviceId || device.name"
								:lat-lng = "[device.latitude, device.longitude]"
								:radius = "resolveMarkerRadius(device)"
								:color = "device.deviceId === selectedDeviceId ? '#FFFFFF' : resolveMarkerColor(device)"
								:fill-color = "resolveMarkerColor(device)"
								:fill-opacity = "0.72"
								:weight = "device.deviceId === selectedDeviceId ? 3 : 1"
								@click = "selectDevice(device)"
							>
								<l-tooltip :options = "{ permanent: false, interactive: true }">
									{{ device.name }} · {{ device.count }}
								</l-tooltip>
							</l-circle-marker>
						</l-map>
						<div v-else class = "dashboard-alerts-by-device-heatmap-widget__placeholder text-center">
							<v-icon large color = "grey lighten-1">mdi-map-marker-off</v-icon>
							<div class = "caption grey--text mt-2">{{ $ml.get('dashboard_alerts_by_device_no_coordinates') }}</div>
						</div>
					</div>

					<div class = "dashboard-alerts-by-device-heatmap-widget__side-list">
						<div class = "caption grey--text text-uppercase mb-2">{{ $ml.get('dashboard_alerts_by_device_ranking') }}</div>
						<div v-if = "!hasDevices" class = "caption grey--text text-center mt-4">{{ $ml.get('dashboard_alerts_by_device_empty') }}</div>
						<div
							v-for = "(device, index) in devices"
							:key = "device.deviceId || device.name || index"
							:class = "['dashboard-alerts-by-device-heatmap-widget__rank-item', { 'dashboard-alerts-by-device-heatmap-widget__rank-item--selected': device.deviceId === selectedDeviceId }]"
							@click = "selectDevice(device)"
						>
							<span class = "dashboard-alerts-by-device-heatmap-widget__rank-number">{{ index + 1 }}</span>
							<div class = "dashboard-alerts-by-device-heatmap-widget__rank-main">
								<div class = "caption white--text text-truncate">{{ device.name }}</div>
								<div class = "dashboard-alerts-by-device-heatmap-widget__bar"><span :style = "{ width: resolveBarWidth(device), background: resolveMarkerColor(device) }"></span></div>
							</div>
							<div class = "text-right">
								<strong class = "white--text">{{ device.count }}</strong>
								<div class = "caption grey--text">{{ device.percentage }}%</div>
							</div>
						</div>
					</div>
				</div>

				<div v-show = "activeView === 'list'" class = "dashboard-alerts-by-device-heatmap-widget__list-only">
					<div v-if = "!hasDevices" class = "dashboard-alerts-by-device-heatmap-widget__empty text-center">
						<v-icon large color = "grey lighten-1">mdi-map-marker-alert</v-icon>
						<div class = "caption grey--text mt-2">{{ $ml.get('dashboard_alerts_by_device_empty') }}</div>
					</div>
					<div v-else class = "dashboard-alerts-by-device-heatmap-widget__heat-list" role = "list">
						<button
							v-for = "(device, index) in devices"
							:key = "device.deviceId || device.name"
							:class = "['dashboard-alerts-by-device-heatmap-widget__heat-row', { 'dashboard-alerts-by-device-heatmap-widget__heat-row--selected': device.deviceId === selectedDeviceId }]"
							:style = "resolveListRowStyle(device)"
							type = "button"
							role = "listitem"
							data-testid = "alerts-by-device-list-row"
							@click = "selectDevice(device)"
						>
							<span class = "dashboard-alerts-by-device-heatmap-widget__heat-rank" :style = "{ borderColor: resolveMarkerColor(device), color: resolveMarkerColor(device) }">#{{ index + 1 }}</span>
							<span class = "dashboard-alerts-by-device-heatmap-widget__heat-main">
								<span class = "dashboard-alerts-by-device-heatmap-widget__heat-name text-truncate">{{ device.name }}</span>
								<span class = "dashboard-alerts-by-device-heatmap-widget__heat-track">
									<span :style = "{ width: resolveBarWidth(device), background: resolveMarkerColor(device) }"></span>
								</span>
							</span>
							<span class = "dashboard-alerts-by-device-heatmap-widget__heat-metrics">
								<strong>{{ device.count }}</strong>
								<small>{{ device.percentage }}%</small>
							</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import { LMap, LTileLayer, LCircleMarker, LTooltip } from 'vue2-leaflet';
import DashboardChartsService from '@/services/dashboard-charts.service.js';
import {
	DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
	DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS,
	DASHBOARD_CHART_INTERVAL_OPTIONS,
	buildDashboardChartTimeRange,
	normalizeDashboardChartIntervalPreset,
} from '@/services/dashboard-pie-events.config.js';

const DEFAULT_CENTER = [-12.046374, -77.042793];

export default {
	name : 'dashboard_alerts_by_device_heatmap_widget',
	components : { LMap, LTileLayer, LCircleMarker, LTooltip },
	props : ['layout', 'layout_id', 'hosts', 'info_to_component', 'layout_end'],
	data(){
		return {
			activeView : 'map',
			devices : [],
			loading : false,
			error : null,
			generatedAt : null,
			refreshTimer : null,
			selectedDeviceId : null,
			selectedInterval : this.getInitialInterval(),
			tileUrl : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
			tileAttribution : '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
			mapZoom : 11,
		};
	},
	computed : {
		intervalOptions(){ return DASHBOARD_CHART_INTERVAL_OPTIONS; },
		streamData(){ return this.info_to_component || this.hosts || {}; },
		timezone(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return config.time && config.time.custom && config.time.custom.tz ? config.time.custom.tz : 'America/Lima';
		},
		requestStreamData(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			return Object.assign({}, this.streamData, {
				config: Object.assign({}, config, {
					time: buildDashboardChartTimeRange(this.selectedInterval, this.timezone),
				}),
			});
		},
		unsupportedSchema(){ return !this.streamData || this.streamData.schemaVersion !== DASHBOARD_PIE_EVENTS_SCHEMA_VERSION; },
		widgetTitle(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const titleKey = (this.streamData && this.streamData.titleKey) || config.titleKey;
			if (titleKey) return this.$ml.get(titleKey);
			return (this.streamData && this.streamData.title) || config.name || config.title || this.$ml.get('dashboard_pie_events_preset_alertas_por_dispositivo');
		},
		refreshSeconds(){
			const config = this.streamData && this.streamData.config ? this.streamData.config : {};
			const value = Number(config.refreshSeconds || this.streamData.refreshSeconds || DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS);
			return value > 0 ? value : DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS;
		},
		hasDevices(){ return this.devices.length > 0; },
		mappableDevices(){ return (this.devices || []).filter(device => this.hasCoordinates(device)); },
		hasMappableDevices(){ return this.mappableDevices.length > 0; },
		maxCount(){ return this.devices.reduce((max, device) => Math.max(max, Number(device.count || 0)), 0); },
		mapCenter(){
			if (!this.hasMappableDevices) return DEFAULT_CENTER;
			const selected = this.mappableDevices.find(device => device.deviceId === this.selectedDeviceId);
			const device = selected || this.mappableDevices[0];
			return [device.latitude, device.longitude];
		},
	},
	watch : {
		streamData : { handler(){ this.resetWidget(); }, deep : true },
		layout_end(){ this.invalidateMapSize(); },
		activeView(){ this.invalidateMapSize(); },
		mappableDevices(){ this.invalidateMapSize(); },
		selectedInterval(){ this.refresh(); },
	},
	mounted(){ this.loadInitialData(); },
	beforeDestroy(){ this.clearRefreshTimer(); },
	methods : {
		loadInitialData(){
			if (this.unsupportedSchema) return;
			if (this.streamData.preview) this.devices = this.normalizeDevices(this.streamData.preview);
			this.refresh();
			this.startRefreshTimer();
		},
		resetWidget(){
			this.clearRefreshTimer();
			this.error = null;
			this.generatedAt = null;
			this.devices = [];
			this.selectedDeviceId = null;
			this.selectedInterval = this.getInitialInterval();
			this.loadInitialData();
		},
		refresh(){
			if (this.unsupportedSchema || this.loading) return;
			this.loading = true;
			this.error = null;

			DashboardChartsService.getPieEventsData(this.requestStreamData).then((response) => {
				if (response && (response.isAxiosError || response.response)) {
					this.error = this.$ml.get('dashboard_pie_events_data_error');
					return;
				}
				const data = this.unwrapResponse(response);
				this.devices = this.normalizeDevices(data);
				this.generatedAt = data && (data.generatedAt || data.generated_at) ? (data.generatedAt || data.generated_at) : null;
				this.restartRefreshTimer(data && (data.nextRefreshSeconds || data.next_refresh_seconds));
				this.invalidateMapSize();
			}).catch(() => {
				this.error = this.$ml.get('dashboard_pie_events_data_error');
			}).finally(() => {
				this.loading = false;
			});
		},
		unwrapResponse(response){
			if (!response) return null;
			if (response.data && response.data.data) return response.data.data;
			if (response.data) return response.data;
			return response;
		},
		normalizeDevices(data){
			const source = data && data.chart ? data.chart : data;
			const rawDevices = source && Array.isArray(source.devices) ? source.devices : this.devicesFromSlices(source && source.slices);
			return rawDevices.map((device, index) => this.normalizeDevice(device, index)).filter(Boolean).sort((a, b) => b.count - a.count);
		},
		devicesFromSlices(slices){
			if (!Array.isArray(slices)) return [];
			return slices.map(slice => ({
				deviceId: slice.deviceId || slice.device_id || slice.key,
				name: slice.name || slice.label || slice.deviceName || slice.device_name,
				count: slice.count !== undefined ? slice.count : slice.value,
				percentage: slice.percentage !== undefined ? slice.percentage : slice.percent,
				latitude: slice.latitude,
				longitude: slice.longitude,
				lastAlertAt: slice.lastAlertAt || slice.last_alert_at,
			}));
		},
		normalizeDevice(device, index){
			if (!device) return null;
			const count = Number(device.count !== undefined ? device.count : (device.value !== undefined ? device.value : 0));
			const percentage = Number(device.percentage !== undefined ? device.percentage : (device.percent !== undefined ? device.percent : 0));
			const latitude = this.normalizeCoordinate(device.latitude !== undefined ? device.latitude : device.lat);
			const longitude = this.normalizeCoordinate(device.longitude !== undefined ? device.longitude : (device.lng !== undefined ? device.lng : (device.lon !== undefined ? device.lon : device.long)));
			return {
				deviceId: device.deviceId || device.device_id || device.id || `device-${index}`,
				name: device.name || device.deviceName || device.device_name || device.label || `${this.$ml.get('dashboard_alerts_by_device_device')} ${index + 1}`,
				latitude,
				longitude,
				count: Number.isFinite(count) ? count : 0,
				percentage: Number.isFinite(percentage) ? Math.round(percentage * 100) / 100 : 0,
				lastAlertAt: device.lastAlertAt || device.last_alert_at || null,
			};
		},
		normalizeCoordinate(value){
			if (value === null || value === undefined || value === '') return null;
			const numeric = Number(value);
			return Number.isFinite(numeric) ? numeric : null;
		},
		hasCoordinates(device){
			return device
				&& device.latitude !== null
				&& device.longitude !== null
				&& Number.isFinite(Number(device.latitude))
				&& Number.isFinite(Number(device.longitude));
		},
		selectDevice(device){
			if (!device) return;
			this.selectedDeviceId = device.deviceId;
			this.activeView = this.hasCoordinates(device) ? 'map' : this.activeView;
			this.invalidateMapSize();
		},
		getInitialInterval(){
			const config = this.info_to_component && this.info_to_component.config ? this.info_to_component.config : {};
			const preset = normalizeDashboardChartIntervalPreset(config.time && config.time.preset ? config.time.preset : 'today');
			return DASHBOARD_CHART_INTERVAL_OPTIONS.some(option => option.value === preset) ? preset : 'today';
		},
		fitMapToDevices(map){
			if (!map || !this.hasMappableDevices || this.activeView !== 'map') return;
			const points = this.mappableDevices.map(device => [Number(device.latitude), Number(device.longitude)]);

			if (points.length === 1) {
				map.setView(points[0], Math.max(this.mapZoom, 15));
				return;
			}

			map.fitBounds(points, {
				padding: [24, 24],
				maxZoom: 16,
			});
		},
		resolveIntensity(device){
			if (!this.maxCount) return 0;
			return Math.max(0.12, Number(device.count || 0) / this.maxCount);
		},
		resolveMarkerRadius(device){ return 7 + Math.round(this.resolveIntensity(device) * 15); },
		resolveMarkerColor(device){
			const intensity = this.resolveIntensity(device);
			if (intensity >= 0.75) return '#EF5350';
			if (intensity >= 0.45) return '#FB8C00';
			return '#FFCA28';
		},
		resolveBarWidth(device){ return `${Math.round(this.resolveIntensity(device) * 100)}%`; },
		resolveListRowStyle(device){
			const intensity = this.resolveIntensity(device);
			return {
				background: `linear-gradient(90deg, rgba(239, 83, 80, ${0.08 + (intensity * 0.18)}) 0%, rgba(20, 23, 31, 0.92) 68%)`,
				borderColor: device.deviceId === this.selectedDeviceId ? this.resolveMarkerColor(device) : `rgba(255, 255, 255, ${0.08 + (intensity * 0.1)})`,
			};
		},
		startRefreshTimer(){
			this.clearRefreshTimer();
			this.refreshTimer = setInterval(() => this.refresh(), this.refreshSeconds * 1000);
		},
		restartRefreshTimer(nextRefreshSeconds){
			if (nextRefreshSeconds && Number(nextRefreshSeconds) > 0 && Number(nextRefreshSeconds) !== this.refreshSeconds) {
				this.clearRefreshTimer();
				this.refreshTimer = setInterval(() => this.refresh(), Number(nextRefreshSeconds) * 1000);
			}
		},
		clearRefreshTimer(){
			if (this.refreshTimer) {
				clearInterval(this.refreshTimer);
				this.refreshTimer = null;
			}
		},
		invalidateMapSize(){
			this.$nextTick(() => {
				const map = this.$refs.map && this.$refs.map.mapObject;
				if (map && map.invalidateSize) {
					map.invalidateSize();
					this.fitMapToDevices(map);
				}
				this.$emit('resized_state');
			});
		},
	},
};
</script>

<style scoped>
@import "~leaflet/dist/leaflet.css";
.dashboard-alerts-by-device-heatmap-widget { overflow: hidden; background: #14171F; isolation: isolate; }
.dashboard-alerts-by-device-heatmap-widget__body { height: calc(100% - 24px); padding: 8px; overflow: hidden; position: relative; }
.dashboard-alerts-by-device-heatmap-widget__content { height: 100%; display: flex; flex-direction: column; }
.dashboard-alerts-by-device-heatmap-widget__intervals { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-bottom: 4px; }
.dashboard-alerts-by-device-heatmap-widget__interval-toggle { border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 8px; overflow: hidden; }
.dashboard-alerts-by-device-heatmap-widget__interval-toggle >>> .v-btn { min-width: 46px !important; padding: 0 8px !important; font-size: 10px; letter-spacing: 0.08em; }
.dashboard-alerts-by-device-heatmap-widget__tabs { flex: 0 0 auto; }
.dashboard-alerts-by-device-heatmap-widget__tabs >>> .v-tab { min-width: 64px; padding: 0 12px; font-size: 12px; letter-spacing: 0.08em; }
.dashboard-alerts-by-device-heatmap-widget__tabs >>> .v-tabs-slider { height: 1px; }
.dashboard-alerts-by-device-heatmap-widget__map-row { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr) 260px; gap: 10px; padding-top: 6px; }
.dashboard-alerts-by-device-heatmap-widget__map-panel { min-height: 160px; overflow: hidden; border-radius: 10px; background: radial-gradient(circle at center, rgba(244, 67, 54, 0.18), rgba(20, 23, 31, 0.92)); border: 1px solid rgba(255, 255, 255, 0.12); position: relative; z-index: 0; isolation: isolate; }
.dashboard-alerts-by-device-heatmap-widget__leaflet { height: 100%; min-height: 160px; background: #10151F; position: relative; z-index: 0; }
.dashboard-alerts-by-device-heatmap-widget__placeholder, .dashboard-alerts-by-device-heatmap-widget__empty { display: flex; height: 100%; min-height: 160px; align-items: center; justify-content: center; flex-direction: column; }
.dashboard-alerts-by-device-heatmap-widget__side-list, .dashboard-alerts-by-device-heatmap-widget__list-only { min-height: 0; overflow: auto; }
.dashboard-alerts-by-device-heatmap-widget__rank-item { display: grid; grid-template-columns: 26px minmax(0, 1fr) 56px; gap: 8px; align-items: center; padding: 7px; border-radius: 8px; cursor: pointer; border: 1px solid transparent; }
.dashboard-alerts-by-device-heatmap-widget__rank-item:hover, .dashboard-alerts-by-device-heatmap-widget__rank-item--selected { background: rgba(255, 255, 255, 0.08); border-color: rgba(239, 83, 80, 0.7); }
.dashboard-alerts-by-device-heatmap-widget__rank-number { display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; color: #FFFFFF; background: rgba(239, 83, 80, 0.35); font-size: 11px; }
.dashboard-alerts-by-device-heatmap-widget__bar { height: 4px; margin-top: 4px; border-radius: 999px; background: rgba(255, 255, 255, 0.12); overflow: hidden; }
.dashboard-alerts-by-device-heatmap-widget__bar span { display: block; height: 100%; border-radius: inherit; }
.dashboard-alerts-by-device-heatmap-widget__heat-list { display: grid; gap: 6px; padding: 2px 0; }
.dashboard-alerts-by-device-heatmap-widget__heat-row { width: 100%; display: grid; grid-template-columns: 42px minmax(0, 1fr) 64px; gap: 10px; align-items: center; min-height: 44px; padding: 7px 9px; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 10px; color: #FFFFFF; cursor: pointer; text-align: left; }
.dashboard-alerts-by-device-heatmap-widget__heat-row:hover, .dashboard-alerts-by-device-heatmap-widget__heat-row--selected { box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16), 0 0 16px rgba(239, 83, 80, 0.14); }
.dashboard-alerts-by-device-heatmap-widget__heat-rank { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 24px; border: 1px solid; border-radius: 999px; background: rgba(0, 0, 0, 0.18); font-size: 11px; font-weight: 700; letter-spacing: 0.04em; }
.dashboard-alerts-by-device-heatmap-widget__heat-main { min-width: 0; display: grid; gap: 5px; }
.dashboard-alerts-by-device-heatmap-widget__heat-name { display: block; font-size: 12px; font-weight: 600; line-height: 1.1; }
.dashboard-alerts-by-device-heatmap-widget__heat-track { height: 5px; overflow: hidden; border-radius: 999px; background: rgba(255, 255, 255, 0.12); }
.dashboard-alerts-by-device-heatmap-widget__heat-track span { display: block; height: 100%; border-radius: inherit; }
.dashboard-alerts-by-device-heatmap-widget__heat-metrics { display: grid; justify-items: end; line-height: 1; }
.dashboard-alerts-by-device-heatmap-widget__heat-metrics strong { font-size: 18px; color: #FFFFFF; }
.dashboard-alerts-by-device-heatmap-widget__heat-metrics small { margin-top: 4px; color: rgba(255, 255, 255, 0.55); font-size: 10px; }
@media (max-width: 960px) { .dashboard-alerts-by-device-heatmap-widget__map-row { grid-template-columns: 1fr; } }
</style>
